import { Link } from 'react-router-dom';
import {ArrowRight, Calendar, MapPin, Wine, Newspaper, ExternalLink, Navigation, Clock} from 'lucide-react';
import { motion } from 'framer-motion';
import { useEffect, useState, useCallback } from 'react';
import { lumi } from '../lib/lumi';
import { computeEventStatus } from '../utils/eventStatus';
import NewsletterSection from '../components/NewsletterSection';
import EditableText from '../components/EditableText';
import { useEditMode } from '../contexts/EditModeContext';
import SEO from '../components/SEO';



const Home = () => {
  const [featuredArtwork, setFeaturedArtwork] = useState<any>(null);
  const [allArtworks, setAllArtworks] = useState<any[]>([]);
  const [homeContent, setHomeContent] = useState<any>({});
  const [upcomingEvents, setUpcomingEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isEditMode } = useEditMode();
  const [eventsContent, setEventsContent] = useState<any>({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [allResult, contentResult] = await Promise.all([
        lumi.entities.artwork.list(),
        lumi.entities.home_content.list(),
      ]);

      const content: any = {};
      contentResult.list?.forEach((item: any) => {
        content[item.section] = item;
      });
      setHomeContent(content);

      const activeArtworks = (allResult.list || []).filter((a: any) => a.isActive !== false);

      const printsSection = content.prints;
      let selectedArtworks: any[] = [];

      const featuredOnes = activeArtworks.filter((artwork: any) => artwork.featured) || [];

      if (featuredOnes.length > 0) {
        selectedArtworks = featuredOnes;
      } else if (printsSection?.selectedArtworks && printsSection.selectedArtworks.length > 0) {
        selectedArtworks = activeArtworks.filter((artwork: any) =>
        printsSection.selectedArtworks.includes(artwork._id)
        ) || [];
        selectedArtworks.sort((a: any, b: any) =>
        printsSection.selectedArtworks.indexOf(a._id) - printsSection.selectedArtworks.indexOf(b._id)
        );
      } else {
        selectedArtworks = activeArtworks.slice(0, 4) || [];
      }

      setAllArtworks(selectedArtworks);
      setFeaturedArtwork(activeArtworks.find((a: any) => a.featured) || activeArtworks[0]);

      try {
        const [eventsResult, eventsContentResult] = await Promise.all([
          lumi.entities.events.list({ sort: { startDate: 1 } }),
          lumi.entities.home_content.list({ filter: { section: 'events' } }),
        ]);
        const allEvents = (eventsResult.list || []).map((e: any) => ({
          ...e,
          status: computeEventStatus(e.startDate, e.endDate, e.status),
        }));
        const upcoming = allEvents.filter((e: any) => e.type !== 'magazine' && (e.status === 'a_venir' || e.status === 'en_cours')).slice(0, 3);
        setUpcomingEvents(upcoming);
        if (eventsContentResult?.list?.[0]) {
          setEventsContent(eventsContentResult.list[0]);
        }
      } catch (err) {
        console.error('Error loading events:', err);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError(error instanceof Error ? error.message : 'Failed to load page data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Helper to update local state after inline edit
  const handleFieldSave = (section: string, field: string, newValue: string) => {
    setHomeContent((prev: any) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: newValue,
      },
    }));
  };

  return (
    <div className="overflow-hidden">
      <SEO
        title="Art for the heart — Original Paintings by Amanaliya"
        description="Art for the heart — original paintings by Amanaliya Art, Swiss-French contemporary artist. Available for sale, worldwide shipping, free delivery in Europe."
        keywords="Amanaliya, Amanaliya Art, contemporary artist, original paintings, abstract art, figurative art, buy art, online gallery, Switzerland, worldwide shipping"
        image={featuredArtwork?.image}
        url="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Amanaliya Art — Contemporary Painter | Original Artworks',
          url: 'https://www.amanaliya.com/',
          description: 'Art for the heart — original paintings by Amanaliya Art, Swiss-French contemporary artist. Available for sale, worldwide shipping, free delivery in Europe.',
          isPartOf: {
            '@type': 'WebSite',
            name: 'Amanaliya Art',
            url: 'https://www.amanaliya.com/',
          },
          about: {
            '@type': 'Person',
            name: 'Amanaliya',
            alternateName: 'Amanaliya Art',
          },
        }}
      />
      {/* Loading State */}
      {loading && (
        <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#722F37] mb-4"></div>
            <p className="text-[#2C2C2C]/60 font-['EB_Garamond'] text-lg">Loading...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] px-6">
          <div className="max-w-md text-center">
            <div className="mb-6">
              <svg className="mx-auto h-16 w-16 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="font-['EB_Garamond'] text-2xl font-bold text-[#2C2C2C] mb-3">Unable to Load Page</h2>
            <p className="text-[#2C2C2C]/60 mb-6">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#8C6D3F] text-white hover:bg-[#6B4E2E] transition-all duration-300"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      {!loading && !error && (
        <>
      {/* Hero Section - Full Screen */}
      <section className="relative min-h-[85vh] sm:min-h-screen flex items-center justify-center">
        {/* Background Image */}
        {featuredArtwork?.image &&
        <div className="absolute inset-0">
            <img
            src={featuredArtwork.image}
            alt={`${featuredArtwork.title} — original painting by Amanaliya`}
            fetchpriority="high"
            className="w-full h-full object-cover"
            style={{ transform: `rotate(${featuredArtwork.rotation || 0}deg)` }} />

            <div className="absolute inset-0 bg-black/30" />
          </div>
        }

        {/* Hero Content */}
        <motion.div
          className="relative z-10 text-center px-4 sm:px-6 max-w-5xl mx-auto py-16 sm:py-0"
          initial={{ opacity: 1, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}>

          <motion.div
            initial={{ opacity: 1, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}>
            <EditableText
              collection="home_content"
              documentId={homeContent.hero?._id || ''}
              field="title"
              value={homeContent.hero?.title || "AMANALIYA"}
              type="text"
              className="font-['EB_Garamond'] text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-4 sm:mb-6 leading-tight tracking-wider"
              onSave={(v) => handleFieldSave('hero', 'title', v)}
              as="h1"
            >
              {homeContent.hero?.title || "AMANALIYA"}
            </EditableText>
          </motion.div>

          {(homeContent.hero?.subtitle || isEditMode) &&
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.7 }}>
              <EditableText
                collection="home_content"
                documentId={homeContent.hero?._id || ''}
                field="subtitle"
                value={homeContent.hero?.subtitle || ''}
                type="text"
                className="text-base sm:text-xl md:text-2xl text-white/90 mb-3 sm:mb-4 tracking-widest uppercase font-light"
                onSave={(v) => handleFieldSave('hero', 'subtitle', v)}
                as="p"
              >
                {homeContent.hero?.subtitle || (isEditMode ? 'Click to add subtitle...' : '')}
              </EditableText>
            </motion.div>
          }

          {(homeContent.hero?.description || isEditMode) &&
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.9 }}>
              <EditableText
                collection="home_content"
                documentId={homeContent.hero?._id || ''}
                field="description"
                value={homeContent.hero?.description || ''}
                type="textarea"
                className="text-base sm:text-lg text-white/80 mb-6 sm:mb-8 max-w-2xl mx-auto"
                onSave={(v) => handleFieldSave('hero', 'description', v)}
                as="div"
              >
                {homeContent.hero?.description ? (
                  <span dangerouslySetInnerHTML={{ __html: homeContent.hero.description }} />
                ) : isEditMode ? (
                  <span className="opacity-50 italic">Click to add description...</span>
                ) : null}
              </EditableText>
            </motion.div>
          }

          <motion.div
            initial={{ opacity: 1, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.1 }}>

            <EditableText
              collection="home_content"
              documentId={homeContent.hero?._id || ''}
              field="buttonText"
              value={homeContent.hero?.buttonText || '— Discover the collection'}
              type="text"
              onSave={(v) => handleFieldSave('hero', 'buttonText', v)}
            >
              <Link
                to="/gallery"
                className="inline-flex items-center gap-3 px-8 sm:px-10 py-3.5 sm:py-4 min-h-[48px] bg-[#8C6D3F] text-white text-base sm:text-lg font-medium hover:bg-[#6B4E2E] transition-all duration-300 shadow-2xl opacity-100"
                style={{ opacity: 1 }}
                onClick={(e) => isEditMode && e.preventDefault()}
              >
                {homeContent.hero?.buttonText || '— Discover the collection'}
              </Link>
            </EditableText>
          </motion.div>
        </motion.div>
      </section>

      {/* Intro Section */}
      {(homeContent.intro || isEditMode) &&
      <section className="py-14 sm:py-20 md:py-24 px-4 sm:px-6 bg-white">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}>

              <EditableText
                collection="home_content"
                documentId={homeContent.intro?._id || ''}
                field="title"
                value={homeContent.intro?.title || ''}
                type="text"
                className="font-['EB_Garamond'] text-3xl sm:text-4xl md:text-5xl font-bold text-[#2C2C2C] mb-4 sm:mb-6"
                onSave={(v) => handleFieldSave('intro', 'title', v)}
                as="h2"
              >
                {homeContent.intro?.title || (isEditMode ? 'Click to add title...' : '')}
              </EditableText>

              {(homeContent.intro?.description || isEditMode) &&
                <EditableText
                  collection="home_content"
                  documentId={homeContent.intro?._id || ''}
                  field="description"
                  value={homeContent.intro?.description || ''}
                  type="textarea"
                  className="text-base sm:text-lg text-[#2C2C2C]/70 leading-relaxed prose prose-base sm:prose-lg max-w-none mx-auto"
                  onSave={(v) => handleFieldSave('intro', 'description', v)}
                  as="div"
                >
                  {homeContent.intro?.description ? (
                    <span dangerouslySetInnerHTML={{ __html: homeContent.intro.description }} />
                  ) : isEditMode ? (
                    <span className="opacity-50 italic">Click to add introduction text...</span>
                  ) : null}
                </EditableText>
              }
            </motion.div>
          </div>
        </section>
      }

      {/* Featured Collection Grid */}
      {allArtworks.length > 0 &&
      <section className="py-14 sm:py-20 md:py-24 px-4 sm:px-6 bg-[#722F37]">
          <div className="max-w-7xl mx-auto">
            <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10 sm:mb-16">

              <EditableText
                collection="home_content"
                documentId={homeContent.prints?._id || ''}
                field="title"
                value={homeContent.prints?.title || 'ORIGINALS'}
                type="text"
                className="font-['EB_Garamond'] text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-2 tracking-[0.15em] sm:tracking-[0.2em] uppercase"
                onSave={(v) => handleFieldSave('prints', 'title', v)}
                as="h2"
              >
                {homeContent.prints?.title || 'ORIGINALS'}
              </EditableText>
            </motion.div>

            <div className={`grid grid-cols-1 sm:grid-cols-2 ${allArtworks.length === 3 ? 'lg:grid-cols-3' : allArtworks.length === 2 ? 'lg:grid-cols-2' : 'lg:grid-cols-4'} gap-6 sm:gap-8`}>
              {allArtworks.map((artwork, index) =>
            <motion.div
              key={artwork._id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: Math.min(index * 0.05, 0.15) }}
              className="flex flex-col">

                  {/* Cadre blanc epais (passe-partout) */}
                  <div className="bg-white p-4 sm:p-6 md:p-8 shadow-2xl relative">
                    <Link to={`/artwork/${artwork._id}`} className="group block">
                      <div className="relative aspect-[3/4] overflow-hidden">
                        <img
                      src={artwork.image}
                      alt={`${artwork.title} — original ${artwork.technique || 'painting'} by Amanaliya`}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                      style={{ transform: `rotate(${artwork.rotation || 0}deg)` }} />

                      </div>
                    </Link>
                    
                    {/* Status stamp (sold/reserved) */}
                    {(artwork.status === 'vendue' || artwork.status === 'reserver') &&
                <div className="absolute top-2 right-2 z-20">
                        <div className={`px-4 py-2 rounded font-bold text-xs uppercase tracking-wider shadow-2xl ${
                  artwork.status === 'vendue' ?
                  'text-white' :
                  'bg-yellow-400 text-gray-900 border-2 border-yellow-600'}`
                  }
                  style={{
                    backgroundColor: artwork.status === 'vendue' ? '#7f1734' : undefined,
                    boxShadow: '0 4px 15px rgba(0,0,0,0.4)'
                  }}>
                          {artwork.status === 'vendue' ? 'SOLD' : 'RESERVED'}
                        </div>
                      </div>
                }
                  </div>


                  
                  {/* Artwork title */}
                  <div className="mt-4 flex flex-col items-center gap-3">
                    <Link
                  to={`/artwork/${artwork._id}`}
                  className="inline-block px-8 py-3 text-white font-['EB_Garamond'] text-sm tracking-[0.2em] uppercase hover:text-white/70 transition-all duration-300">
                      {artwork.title}
                    </Link>
                  </div>
                </motion.div>
            )}
            </div>
          </div>
        </section>
      }

      {/* About Section */}
      <section className="py-14 sm:py-20 md:py-24 px-4 sm:px-6 bg-[#FAF9F6]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}>

            <EditableText
              collection="home_content"
              documentId={homeContent.about?._id || ''}
              field="title"
              value={homeContent.about?.title || 'About the Artist'}
              type="text"
              className="font-['EB_Garamond'] text-3xl sm:text-4xl md:text-5xl font-bold text-[#722F37] mb-4 sm:mb-6"
              onSave={(v) => handleFieldSave('about', 'title', v)}
              as="h2"
            >
              {homeContent.about?.title || 'About the Artist'}
            </EditableText>

            {(homeContent.about?.description || isEditMode) ? (
              <EditableText
                collection="home_content"
                documentId={homeContent.about?._id || ''}
                field="description"
                value={homeContent.about?.description || ''}
                type="textarea"
                className="text-lg text-[#2C2C2C]/70 mb-8 leading-relaxed prose prose-lg max-w-none mx-auto"
                onSave={(v) => handleFieldSave('about', 'description', v)}
              >
                {homeContent.about?.description ? (
                  <span dangerouslySetInnerHTML={{ __html: homeContent.about.description }} />
                ) : (
                  <span className="opacity-50 italic">Click to add description...</span>
                )}
              </EditableText>
            ) : (
              <p className="text-lg text-[#2C2C2C]/70 mb-8 leading-relaxed">
                Each artwork is a unique exploration of color, texture, and emotion. My work invites contemplation and the discovery of inner worlds.
              </p>
            )}

            <Link
              to={homeContent.about?.buttonLink || '/about'}
              className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 min-h-[48px] bg-[#8C6D3F] text-white hover:bg-[#6B4E2E] transition-all duration-300">

              {homeContent.about?.buttonText || 'Learn more'}
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Newsletter Section */}
      <NewsletterSection />

      {/* Upcoming Events Section */}
      {upcomingEvents.length > 0 && (
        <section className="py-14 sm:py-20 md:py-24 px-4 sm:px-6 bg-white">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-14"
            >
              <EditableText
                collection="home_content"
                documentId={eventsContent?._id || ''}
                field="subtitle"
                value={eventsContent?.subtitle || 'Agenda'}
                type="text"
                className="text-[#8B7355] text-sm tracking-[0.3em] uppercase mb-3 block"
                onSave={(v) => setEventsContent((prev: any) => ({ ...prev, subtitle: v }))}
                as="span"
              >
                {eventsContent?.subtitle || 'Agenda'}
              </EditableText>
              <EditableText
                collection="home_content"
                documentId={eventsContent?._id || ''}
                field="title"
                value={eventsContent?.title || 'Upcoming Events'}
                type="text"
                className="font-['EB_Garamond'] text-3xl sm:text-4xl md:text-5xl font-bold text-[#2C2C2C]"
                onSave={(v) => setEventsContent((prev: any) => ({ ...prev, title: v }))}
                as="h2"
              >
                {eventsContent?.title || 'Upcoming Events'}
              </EditableText>
            </motion.div>

            <div className="space-y-6">
              {upcomingEvents.map((event: any, index: number) => {
                const hasDateRange = event.startDate && event.endDate;
                const startDate = hasDateRange ? new Date(event.startDate) : null;
                const endDate = hasDateRange ? new Date(event.endDate) : null;
                const isOngoing = hasDateRange && computeEventStatus(event.startDate, event.endDate, event.status) === 'en_cours';
                const isMagazine = event.type === 'magazine';
                const hasMagazineCover = isMagazine && !!event.magazineCoverImage;
                const hasEventImage = !isMagazine && !!event.image;
                const hasLink = !!event.externalLink;

                return (
                  <motion.div
                    key={event._id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.15) }}
                    className="flex flex-col md:flex-row border border-[#D4C5B9]/30 bg-[#FAF9F6] hover:shadow-md transition-all duration-300 overflow-hidden"
                  >
                    {/* LEFT: Visual area */}
                    {isMagazine && hasMagazineCover ? (
                      /* Magazine: cover as main visual with article overlay */
                      <div className="relative flex-shrink-0 w-full md:w-52 lg:w-60 bg-[#F5F0EB] flex items-center justify-center p-5 md:p-6">
                        <a
                          href={hasLink ? event.externalLink : undefined}
                          target={hasLink ? '_blank' : undefined}
                          rel={hasLink ? 'noopener noreferrer' : undefined}
                          className={`relative z-10 block ${hasLink ? 'cursor-pointer' : 'cursor-default'}`}
                        >
                          <div
                            className="w-32 lg:w-36 bg-white rounded-sm overflow-hidden transition-transform duration-500 hover:scale-[1.03]"
                            style={{
                              boxShadow: '6px 10px 24px rgba(0,0,0,0.16), 2px 3px 6px rgba(0,0,0,0.06)',
                              transform: 'rotate(-2deg)',
                            }}
                          >
                            <img src={event.magazineCoverImage} alt={`${event.title} - Cover`} className="w-full h-auto object-contain" loading="lazy" decoding="async" />
                          </div>
                        </a>
                        {event.magazineArticleImage && (
                          <a
                            href={hasLink ? event.externalLink : undefined}
                            target={hasLink ? '_blank' : undefined}
                            rel={hasLink ? 'noopener noreferrer' : undefined}
                            className={`absolute bottom-2 right-2 md:bottom-3 md:right-3 z-20 block ${hasLink ? 'cursor-pointer' : 'cursor-default'}`}
                          >
                            <div
                              className="w-20 lg:w-24 bg-white rounded-sm overflow-hidden transition-transform duration-500 hover:scale-[1.05]"
                              style={{
                                boxShadow: '3px 5px 16px rgba(0,0,0,0.18)',
                                transform: 'rotate(3deg)',
                              }}
                            >
                              <img src={event.magazineArticleImage} alt={`${event.title} - Article`} className="w-full h-auto object-contain" loading="lazy" decoding="async" />
                            </div>
                          </a>
                        )}
                        <div className="absolute top-2 left-2 z-30">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-white/90 backdrop-blur-sm text-[9px] font-semibold text-[#8B7355] tracking-wider uppercase rounded-sm shadow-sm">
                            <Newspaper size={10} />
                            Press
                          </span>
                        </div>
                      </div>
                    ) : hasEventImage ? (
                      /* Exhibition: event image as left visual with date overlay, size adapts to the image */
                      <Link
                        to="/actualites"
                        className="relative flex-shrink-0 w-full md:w-52 lg:w-60 flex items-center justify-center bg-[#F5F0EB] overflow-hidden group"
                      >
                        <img
                          src={event.image}
                          alt={`${event.title} — exhibition poster`}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-auto max-h-72 object-contain transition-transform duration-700 group-hover:scale-105"
                        />
                        {startDate && (
                          <div className="absolute top-3 left-3 bg-[#722F37]/90 backdrop-blur-sm text-white px-2.5 py-1.5 text-center rounded-sm">
                            <span className="text-xl font-bold leading-none block">{startDate.toLocaleDateString('en-US', { day: 'numeric' })}</span>
                            <span className="text-[9px] tracking-[0.15em] uppercase text-white/80">{startDate.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()}</span>
                          </div>
                        )}
                      </Link>
                    ) : startDate ? (
                      /* No image: date block */
                      <div className="flex-shrink-0 w-full md:w-24 bg-[#722F37] text-white flex flex-row md:flex-col items-center justify-center py-4 md:py-8 gap-2 md:gap-0">
                        <span className="text-3xl md:text-4xl font-bold leading-none">
                          {startDate.toLocaleDateString('en-US', { day: 'numeric' })}
                        </span>
                        <span className="text-xs tracking-[0.2em] uppercase text-white/80">
                          {startDate.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()}
                        </span>
                      </div>
                    ) : null}

                    {/* RIGHT: Content */}
                    <div className="flex-1 p-5 md:p-6 flex flex-col justify-center">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        {isOngoing ? (
                          <span className="px-2 py-0.5 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                            Ongoing
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                            Upcoming
                          </span>
                        )}
                        {isMagazine && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-[#F5F0EB] text-[#8B7355] border border-[#D4C5B9]/40">
                            Magazine
                          </span>
                        )}
                      </div>

                      <h3 className="font-['EB_Garamond'] text-xl md:text-2xl font-bold text-[#2C2C2C] mb-1">
                        {event.title}
                      </h3>
                      {event.titleFr && event.titleFr !== event.title && (
                        <p className="font-['EB_Garamond'] italic text-sm text-[#722F37]/70 mb-1.5">
                          {event.titleFr}
                        </p>
                      )}

                      {/* Description snippet (2 lines max) */}
                      {event.description && (
                        <p className="text-sm text-[#2C2C2C]/55 leading-relaxed mb-3 line-clamp-2">
                          {event.description}
                        </p>
                      )}

                      <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-[#2C2C2C]/60">
                        {startDate && endDate && (
                          <span className="flex items-center gap-1.5">
                            <Calendar size={13} className="text-[#8B7355]" />
                            {startDate.toLocaleDateString('en-US', { day: 'numeric', month: 'long' })} — {endDate.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </span>
                        )}
                        {event.location && (
                          event.address ? (
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                `${event.address}${event.city ? ', ' + event.city : ''}${event.country ? ', ' + event.country : ''}`
                              )}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1.5 text-[#722F37] hover:text-[#5e2730] transition-colors cursor-pointer"
                            >
                              <MapPin size={13} />
                              {event.location}{event.city ? `, ${event.city}` : ''}
                              <Navigation size={11} className="ml-0.5 flex-shrink-0" />
                            </a>
                          ) : (
                            <span className="flex items-center gap-1.5">
                              <MapPin size={13} className="text-[#8B7355]" />
                              {event.location}{event.city ? `, ${event.city}` : ''}
                            </span>
                          )
                        )}
                        {event.openingHours && (
                          <span className="flex items-center gap-1.5">
                            <Clock size={13} className="text-[#8B7355]" />
                            {event.openingHours}
                          </span>
                        )}
                        {event.vernissageDate && (
                          <span className="flex items-center gap-1.5 text-[#722F37]">
                            <Wine size={13} />
                            Vernissage: {new Date(event.vernissageDate).toLocaleDateString('en-US', { day: 'numeric', month: 'long' })}
                            {event.vernissageTime ? ` \u00b7 ${event.vernissageTime}` : ''}
                          </span>
                        )}
                      </div>
                      {event.externalLink && (
                        <a
                          href={event.externalLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-[#722F37] hover:text-[#5e2730] transition-colors w-fit"
                        >
                          More information <ExternalLink size={13} />
                        </a>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-center mt-10"
            >
              <EditableText
                collection="home_content"
                documentId={eventsContent?._id || ''}
                field="buttonText"
                value={eventsContent?.buttonText || 'View all events'}
                type="text"
                onSave={(v) => setEventsContent((prev: any) => ({ ...prev, buttonText: v }))}
              >
                <Link
                  to={eventsContent?.buttonLink || '/actualites'}
                  className="inline-flex items-center gap-2 px-8 py-4 border-2 border-[#8C6D3F] text-[#8C6D3F] hover:bg-[#8C6D3F] hover:text-white transition-all duration-300"
                  onClick={(e) => isEditMode && e.preventDefault()}
                >
                  {eventsContent?.buttonText || 'View all events'}
                  <ArrowRight size={18} />
                </Link>
              </EditableText>
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-14 sm:py-20 md:py-24 px-4 sm:px-6 bg-[#2C2C2C]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}>

            <EditableText
              collection="home_content"
              documentId={homeContent.cta?._id || ''}
              field="title"
              value={homeContent.cta?.title || 'Interested in an Artwork?'}
              type="text"
              className="font-['EB_Garamond'] text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6"
              onSave={(v) => handleFieldSave('cta', 'title', v)}
              as="h2"
            >
              {homeContent.cta?.title || 'Interested in an Artwork?'}
            </EditableText>

            {(homeContent.cta?.description || isEditMode) ? (
              <EditableText
                collection="home_content"
                documentId={homeContent.cta?._id || ''}
                field="description"
                value={homeContent.cta?.description || ''}
                type="textarea"
                className="text-lg text-white/70 mb-8 leading-relaxed prose prose-lg prose-invert max-w-none mx-auto"
                onSave={(v) => handleFieldSave('cta', 'description', v)}
              >
                {homeContent.cta?.description ? (
                  <span dangerouslySetInnerHTML={{ __html: homeContent.cta.description }} />
                ) : (
                  <span className="opacity-50 italic">Click to add description...</span>
                )}
              </EditableText>
            ) : (
              <p className="text-lg text-white/70 mb-8 leading-relaxed">
                Contact me for any questions about available artworks, custom commissions, or prints.
              </p>
            )}

            <Link
              to={homeContent.cta?.buttonLink || '/contact'}
              className="inline-flex items-center gap-3 px-8 sm:px-10 py-3.5 sm:py-4 min-h-[48px] bg-[#8C6D3F] text-white hover:bg-[#6B4E2E] transition-all duration-300">

              {homeContent.cta?.buttonText || 'Contact me'}
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
        </>
      )}
    </div>);

};

export default Home;
